/**
 * Persist the opaque bearer session issued by the API.
 *
 * The backend mints an `<id>.<secret>` session token and returns it in the
 * response body, so the SPA stores it here and attaches it as
 * `Authorization: Bearer …` on every request.
 */
const STORAGE_KEY = 'aims.session'

export interface StoredSession {
  token: string
  expiresAt: string
}

function isStoredSession(value: unknown): value is StoredSession {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as StoredSession).token === 'string' &&
    typeof (value as StoredSession).expiresAt === 'string'
  )
}

/** Read the stored session, discarding it when expired or malformed. */
export function getStoredSession(): StoredSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isStoredSession(parsed)) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (Number.isFinite(Date.parse(parsed.expiresAt)) && Date.parse(parsed.expiresAt) <= Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    window.localStorage.removeItem(STORAGE_KEY)
    return null
  }
}

export function getStoredToken(): string | null {
  return getStoredSession()?.token ?? null
}

export function setStoredSession(session: StoredSession): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
}

export function clearStoredSession(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(STORAGE_KEY)
}
